import posthog from 'posthog-js';
// Treat a token as expired slightly before its exp so it stays valid for the round trip it is about to
// be used on, and to absorb clock skew between the browser and the IdP.
const EXPIRY_TOLERANCE_MS = 5 * 60 * 1000;
const isTokenExpired = (expiresAt) => Date.now() + EXPIRY_TOLERANCE_MS > expiresAt;
// exp is seconds since epoch; claims without one are treated as unusable rather than assumed fresh.
const isUsable = (claims) => !!claims?.exp && !isTokenExpired(claims.exp * 1000);
export class Auth0IDTokenProvider {
    accessors;
    // Registered from the React flow itself when <Auth0Provider> initializes — useAuth0() is only
    // available inside the provider tree, so the SDK functions are injected rather than imported.
    registerTokenAccessors(accessors) {
        this.accessors = accessors;
    }
    // auth0-spa-js can't be relied on for managing ID token refreshes — its ID token cache entry has no
    // expiry of its own, so getIDTokenClaims() could return an ID token that expired hours ago. Check the
    // ID token's own exp and escalate through the two getAccessTokenSilently modes when it's spent.
    async fetchRaw() {
        if (!this.accessors) {
            // Returns no token and throws nothing, so upstream signs the session out with no error to report.
            console.error('Auth0 token accessors are not registered, cannot fetch an ID token');
            return undefined;
        }
        try {
            const cached = await this.accessors.getIDTokenClaims();
            if (isUsable(cached)) {
                return cached.__raw;
            }
            // Go through the SDK cache first. auth0-spa-js re-checks that cache *inside* its cross-tab lock,
            // so when another tab is mid-exchange we get its result rather than queueing a second
            // refresh-token exchange behind a 5s lock timeout — a timeout here surfaces as a sign-out.
            // The ID token it returns can still be the stale one, hence the re-check below.
            await this.accessors.getAccessTokenSilently({ detailedResponse: true });
            const shared = await this.accessors.getIDTokenClaims();
            if (isUsable(shared)) {
                return shared.__raw;
            }
            // Only reached when the access token outlives the ID token, so the call above was a cache hit
            // that left the stale ID token in place. cacheMode 'off' bypasses the cache entirely and
            // detailedResponse hands back the new id_token directly.
            const refreshed = await this.accessors.getAccessTokenSilently({ cacheMode: 'off', detailedResponse: true });
            if (!refreshed) {
                console.warn('Auth session ceiling reached, no ID token available');
                return undefined;
            }
            return refreshed.id_token;
        }
        catch (error) {
            console.error('Error fetching auth0 token', error);
            posthog.captureException(error);
            return undefined;
        }
    }
}
// Null-object provider for apps that run without authentication (wayste-dashboard). Never returns a token,
// so if auth setup ever runs it falls into the normal "no token found" sign-out path. Do NOT use in apps
// that authenticate users.
export class NoAuthIDTokenProvider {
    async fetchRaw() {
        return undefined;
    }
}
//# sourceMappingURL=id-token-provider.service.js.map